import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MCPTool, MCPToolCreate, AuthConfig, AUTH_MODE_OPTIONS, AuthType } from '@/types/mcpTool';
import { MCPToolsService } from '@/api/services/mcpToolsService';
import { Loader2, CheckCircle2, ArrowLeft, ArrowRight } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface CreateMCPToolDialogProps {
  initialTool?: MCPTool | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export const CreateMCPToolDialog: React.FC<CreateMCPToolDialogProps> = ({
  open,
  initialTool,
  onOpenChange,
  onSuccess,
}) => {
  const [step, setStep] = useState(1);
  const [replaceAuth, setReplaceAuth] = useState(false);
  useEffect(() => {
    if (open) {
      resetForm();
      setName(initialTool?.name || ''); setDescription(initialTool?.description || '');
      setBaseUrl(initialTool?.base_url || ''); setAuthType(initialTool?.auth_type || 'none');
      setReplaceAuth(!initialTool);
    }
  }, [open, initialTool]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [authType, setAuthType] = useState<AuthType>('none');
  const [authHeaders, setAuthHeaders] = useState<Record<string, string>>({});
  const [authQuery, setAuthQuery] = useState<Record<string, string>>({});
  const [credentials, setCredentials] = useState<Record<string, string>>({});

  const resetForm = () => {
    setStep(1);
    setName('');
    setDescription('');
    setBaseUrl('');
    setAuthType('none');
    setAuthHeaders({});
    setAuthQuery({});
    setCredentials({});
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleNext = () => {
    setError(null);
    if (step === 1) {
      // Validate basic info
      if (!name.trim()) {
        setError('Tool name is required');
        return;
      }
      if (!baseUrl.trim()) {
        setError('Base URL is required');
        return;
      }
      if (!/^https?:\/\/.+/.test(baseUrl)) {
        setError('Base URL must start with http:// or https://');
        return;
      }
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setError(null);
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const authConfig: AuthConfig = {
        auth_type: authType,
      };

      if (authType !== 'none') {
        if (Object.keys(authHeaders).length > 0) {
          authConfig.headers = authHeaders;
        }
        if (Object.keys(authQuery).length > 0) {
          authConfig.query_params = authQuery;
        }
        if (Object.keys(credentials).length > 0) {
          authConfig.credentials = credentials;
        }
      }

      const toolData: MCPToolCreate = {
        name: name.trim(),
        description: description.trim(),
        base_url: baseUrl.trim(),
        auth_config: replaceAuth ? authConfig : undefined,
      };

      if (initialTool) await MCPToolsService.updateMCPTool(initialTool.id!, toolData);
      else await MCPToolsService.createMCPTool(toolData);
      onSuccess?.();
      handleClose();
    } catch (err: any) {
      console.error('Error creating MCP tool:', err);
      setError(err.response?.data?.message || err.message || 'Failed to create tool');
    } finally {
      setLoading(false);
    }
  };

  const addHeader = () => {
    setAuthHeaders({ ...authHeaders, '': '' });
  };

  const updateHeaderKey = (oldKey: string, newKey: string) => {
    const newHeaders = { ...authHeaders };
    if (oldKey !== newKey) {
      delete newHeaders[oldKey];
      newHeaders[newKey] = authHeaders[oldKey] || '';
    }
    setAuthHeaders(newHeaders);
  };

  const updateHeaderValue = (key: string, value: string) => {
    setAuthHeaders({ ...authHeaders, [key]: value });
  };

  const removeHeader = (key: string) => {
    const newHeaders = { ...authHeaders };
    delete newHeaders[key];
    setAuthHeaders(newHeaders);
  };

  const addCredential = () => {
    setCredentials({ ...credentials, '': '' });
  };

  const updateCredentialKey = (oldKey: string, newKey: string) => {
    const newCreds = { ...credentials };
    if (oldKey !== newKey) {
      delete newCreds[oldKey];
      newCreds[newKey] = credentials[oldKey] || '';
    }
    setCredentials(newCreds);
  };

  const updateCredentialValue = (key: string, value: string) => {
    setCredentials({ ...credentials, [key]: value });
  };

  const removeCredential = (key: string) => {
    const newCreds = { ...credentials };
    delete newCreds[key];
    setCredentials(newCreds);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initialTool ? 'Edit' : 'Create'} MCP Tool - Step {step} of 3</DialogTitle>
          <DialogDescription>
            {step === 1 && 'Enter basic information about your tool'}
            {step === 2 && 'Configure authentication for API calls'}
            {step === 3 && 'Review and create your tool'}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Tool Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                disabled={!!initialTool}
                placeholder="stripe_api"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                Unique identifier (use lowercase, underscores, no spaces)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Stripe payment processing API"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="baseUrl">
                Base URL <span className="text-red-500">*</span>
              </Label>
              <Input
                id="baseUrl"
                placeholder="https://api.stripe.com"
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                The root URL for all API endpoints
              </p>
            </div>
          </div>
        )}

        {/* Step 2: Authentication */}
        {step === 2 && (
          <div className="space-y-4">
            {initialTool && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={replaceAuth} onChange={e => setReplaceAuth(e.target.checked)} />Replace saved authentication</label>}
            {initialTool && !replaceAuth && <p className="text-sm text-muted-foreground">Saved credentials will be kept.</p>}
            <fieldset disabled={!replaceAuth} className="space-y-4 disabled:opacity-50">
            <div className="space-y-2">
              <Label>Authentication Type</Label>
              <Select value={authType} onValueChange={(v) => setAuthType(v as AuthType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUTH_MODE_OPTIONS.filter(option => option.value !== 'oauth2_per_user').map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div>
                        <div className="font-medium">{option.label}</div>
                        <div className="text-xs text-gray-500">{option.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {authType !== 'none' && (
              <>
                {/* Headers */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Headers</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addHeader}>
                      Add Header
                    </Button>
                  </div>
                  {Object.entries(authHeaders).map(([key, value]) => (
                    <div key={key} className="flex gap-2">
                      <Input
                        placeholder="Header name"
                        value={key}
                        onChange={(e) => updateHeaderKey(key, e.target.value)}
                      />
                      <Input
                        type="password"
                        placeholder="Value (use {variable} for templates)"
                        value={value}
                        onChange={(e) => updateHeaderValue(key, e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeHeader(key)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500">
                    Example: Authorization = Bearer {'{api_key}'}
                  </p>
                </div>

                {authType === 'api_key_query' && <div className="space-y-2">
                  <Label>Query parameter</Label>
                  <Input aria-label="Query authentication name" placeholder="api_key" value={Object.keys(authQuery)[0] || ''} onChange={e => setAuthQuery({[e.target.value]:Object.values(authQuery)[0] || ''})} />
                  <Input aria-label="Query authentication value" type="password" placeholder="Secret or {credential_name}" value={Object.values(authQuery)[0] || ''} onChange={e => setAuthQuery({[Object.keys(authQuery)[0] || 'api_key']:e.target.value})} />
                </div>}

                {/* Credentials */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Credentials (Secrets)</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addCredential}>
                      Add Credential
                    </Button>
                  </div>
                  {Object.entries(credentials).map(([key, value]) => (
                    <div key={key} className="flex gap-2">
                      <Input
                        placeholder="Variable name (e.g., api_key)"
                        value={key}
                        onChange={(e) => updateCredentialKey(key, e.target.value)}
                      />
                      <Input
                        type="password"
                        placeholder="Actual secret value"
                        value={value}
                        onChange={(e) => updateCredentialValue(key, e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeCredential(key)}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <p className="text-xs text-gray-500">
                    These will be interpolated into header/query templates
                  </p>
                </div>
              </>
            )}
            </fieldset>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 3 && (
          <div className="space-y-4">
            <div className="rounded-lg border p-4 space-y-3">
              <div>
                <p className="text-sm font-medium text-gray-500">Tool Name</p>
                <p className="font-medium">{name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Description</p>
                <p>{description || <span className="text-gray-400">None</span>}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Base URL</p>
                <p>{baseUrl}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Authentication</p>
                <p className="capitalize">{authType.replace('_', ' ')}</p>
              </div>
              {Object.keys(authHeaders).length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-500">Headers</p>
                  <ul className="list-disc list-inside text-sm">
                    {Object.entries(authHeaders).map(([key, value]) => (
                      <li key={key}>
                        <code className="text-xs bg-gray-100 px-1 rounded">{key}</code>: ••••••••
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          {step > 1 && (
            <Button type="button" variant="outline" onClick={handleBack} disabled={loading}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
          {step < 3 ? (
            <Button type="button" onClick={handleNext}>
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button type="button" onClick={handleSubmit} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  {initialTool ? 'Save Changes' : 'Create Tool'}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
